import React, { useState, useEffect, useRef, useCallback } from 'react';
import PatternGrid from './PatternGrid';
import { loadSamples, playGuitarStrum, playMetronome } from './useAudio';

function buildNotesStr(rhythmNum, rhythmDen, noteLength, totalSteps) {
	const stepsPerBeat = Math.max(1, Math.round(noteLength / rhythmDen));
	const notes = new Array(totalSteps).fill('');
	for (let i = 0; i < rhythmNum; i++) {
		const beatStart = Math.round(i * stepsPerBeat);
		if (beatStart < totalSteps) notes[beatStart] = String(i + 1);
		if (stepsPerBeat >= 2) {
			const half = Math.floor(stepsPerBeat / 2);
			if (beatStart + half < totalSteps) notes[beatStart + half] = '&';
		}
	}
	return notes;
}

function buildEmptyPattern(rhythmNum, rhythmDen, noteLength = 8) {
	const stepsPerBeat = Math.max(1, Math.round(noteLength / rhythmDen));
	return new Array(rhythmNum * stepsPerBeat).fill('');
}

function computeIntervalMs(bpm, rhythmDen, noteLength) {
	const stepsPerBeat = Math.max(1, Math.round(noteLength / rhythmDen));
	return (60 / bpm) / stepsPerBeat * 1000;
}


const PlayIcon = () => (
	<svg width="28" height="28" viewBox="0 0 24 24">
		<circle cx="12" cy="12" r="11" fill="white" />
		<polygon points="10 8 16 12 10 16 10 8" fill="#1e1e1e" />
	</svg>
);
const StopIcon = () => (
	<svg width="28" height="28" viewBox="0 0 24 24">
		<circle cx="12" cy="12" r="11" fill="white" />
		<rect x="9" y="9" width="6" height="6" fill="#1e1e1e" />
	</svg>
);
const MiniPlayIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
		<polygon points="7 4 20 12 7 20 7 4" />
	</svg>
);
const MiniStopIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
		<rect x="6" y="5" width="4" height="14" /><rect x="14" y="5" width="4" height="14" />
	</svg>
);

export default function StrummingEditor({ song_id, initial_patterns, apiBase = 'api/', apiUser = '', apiKey = '' }) {
	const [patterns, setPatterns] = useState(initial_patterns || []);
	const [editingIdx, setEditingIdx] = useState(null);

	const [name, setName] = useState('');
	const [rhythmNum, setRhythmNum] = useState(4);
	const [rhythmDen, setRhythmDen] = useState(4);
	const [noteLength, setNoteLength] = useState(8);
	const [bpm, setBpm] = useState(100);
	const [patternStr, setPatternStr] = useState(buildEmptyPattern(4, 4, 8));
	const [focusedIndex, setFocusedIndex] = useState(0);
	const [isPlaying, setIsPlaying] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const [saving, setSaving] = useState(false);
	const [metronomeEnabled, setMetronomeEnabled] = useState(false);
	const [templates, setTemplates] = useState([]);
	const [templatesOpen, setTemplatesOpen] = useState(false);
	const [playingTemplateIdx, setPlayingTemplateIdx] = useState(null);

	const intervalRef = useRef(null);
	const idxRef = useRef(-1);
	const patternStrRef = useRef(patternStr);
	const rhythmDenRef = useRef(rhythmDen);
	const noteLengthRef = useRef(noteLength);
	const metronomeRef = useRef(metronomeEnabled);
	const templateIntervalRef = useRef(null);
	const templateIdxRef = useRef(-1);
	const dragSrcIdx = useRef(null);

	useEffect(() => {
		fetch(`${apiBase}get-strumming-templates.html`)
			.then(r => r.json())
			.then(d => { if (d.status === 'success') setTemplates(d.response || []); })
			.catch(() => { });
	}, []);

	useEffect(() => { patternStrRef.current = patternStr; }, [patternStr]);
	useEffect(() => { rhythmDenRef.current = rhythmDen; }, [rhythmDen]);
	useEffect(() => { noteLengthRef.current = noteLength; }, [noteLength]);
	useEffect(() => { metronomeRef.current = metronomeEnabled; }, [metronomeEnabled]);
	useEffect(() => { loadSamples(); }, []);

	const notesStr = buildNotesStr(rhythmNum, rhythmDen, noteLength, patternStr.length);

	function stopPlayback() {
		clearInterval(intervalRef.current);
		idxRef.current = -1;
		setIsPlaying(false);
		setActiveIndex(-1);
	}

	function stopTemplatePreview() {
		clearInterval(templateIntervalRef.current);
		templateIntervalRef.current = null;
		templateIdxRef.current = -1;
		setPlayingTemplateIdx(null);
	}

	function loadPattern(idx) {
		stopPlayback();
		stopTemplatePreview();
		const p = patterns[idx];
		setEditingIdx(idx);
		setName(p.name);
		const [num, den] = p.rhythm.split('/').map(Number);
		setRhythmNum(num);
		setRhythmDen(den);
		setNoteLength(p.note_length || 8);
		setBpm(p.bpm || 100);
		const pat = Array.isArray(p.pattern) ? p.pattern : JSON.parse(p.pattern);
		setPatternStr(pat);
		setFocusedIndex(0);
	}

	function newPattern() {
		stopPlayback();
		stopTemplatePreview();
		setEditingIdx(null);
		setName('');
		setRhythmNum(4);
		setRhythmDen(4);
		setNoteLength(8);
		setBpm(100);
		setPatternStr(buildEmptyPattern(4, 4, 8));
		setFocusedIndex(0);
	}

	function loadFromTemplate(tpl) {
		stopPlayback();
		stopTemplatePreview();
		const [num, den] = tpl.rhythm.split('/').map(Number);
		const nLen = tpl.note_length || tpl.noteLength || 8;
		setName(tpl.name);
		setRhythmNum(num);
		setRhythmDen(den);
		setNoteLength(nLen);
		setBpm(tpl.bpm || 100);
		const pat = Array.isArray(tpl.pattern) ? tpl.pattern : JSON.parse(tpl.pattern);
		setPatternStr([...pat]);
		setFocusedIndex(0);
		setEditingIdx(null);
	}

	function updateRhythm(num, den, nLen = noteLength) {
		const stepsPerBeat = Math.max(1, Math.round(nLen / den));
		const totalSteps = num * stepsPerBeat;
		const newPat = new Array(totalSteps).fill('');
		for (let i = 0; i < Math.min(patternStr.length, totalSteps); i++) {
			newPat[i] = patternStr[i];
		}
		setRhythmNum(num);
		setRhythmDen(den);
		setPatternStr(newPat);
		setFocusedIndex(0);
		stopPlayback();
	}

	function updateNoteLength(nLen) {
		setNoteLength(nLen);
		updateRhythm(rhythmNum, rhythmDen, nLen);
	}

	function handleCellClick(idx) {
		if (focusedIndex === idx) {
			const cycle = { D: 'U', U: 'B', B: 'X', X: '', '': 'D' };
			const newPat = [...patternStr];
			newPat[idx] = cycle[patternStr[idx]] ?? 'D';
			setPatternStr(newPat);
		} else {
			setFocusedIndex(idx);
		}
	}

	const handleKeyDown = useCallback(e => {
		if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
		if (!document.querySelector('.sp-editor-wrap')) return;

		const cur = patternStrRef.current;
		const len = cur.length;
		let newPat = null;
		let newFocus = focusedIndex;
		let handled = true;

		if (e.key === 'ArrowRight') { newFocus = (focusedIndex + 1) % len; }
		else if (e.key === 'ArrowLeft') { newFocus = (focusedIndex - 1 + len) % len; }
		else if (e.key === 'ArrowUp') { newPat = [...cur]; newPat[focusedIndex] = 'U'; }
		else if (e.key === 'ArrowDown') { newPat = [...cur]; newPat[focusedIndex] = 'D'; }
		else if (e.key.toLowerCase() === 'x') { newPat = [...cur]; newPat[focusedIndex] = 'X'; }
		else if (e.key.toLowerCase() === 'b') { newPat = [...cur]; newPat[focusedIndex] = 'B'; }
		else if (e.key === ' ' || e.key === 'Spacebar') { newPat = [...cur]; newPat[focusedIndex] = ''; newFocus = (focusedIndex + 1) % len; }
		else if (e.key === 'Backspace' || e.key === 'Delete') { newPat = [...cur]; newPat[focusedIndex] = ''; }
		else handled = false;

		if (handled) {
			e.preventDefault();
			if (newPat) setPatternStr(newPat);
			setFocusedIndex(newFocus);
		}
	}, [focusedIndex]);

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [handleKeyDown]);

	function togglePlay() {
		if (isPlaying) { stopPlayback(); return; }
		stopTemplatePreview();
		setIsPlaying(true);
		idxRef.current = -1;

		const tick = () => {
			idxRef.current = (idxRef.current + 1) % patternStrRef.current.length;
			setActiveIndex(idxRef.current);
			const dir = patternStrRef.current[idxRef.current];
			if (dir) playGuitarStrum(dir);
			if (metronomeRef.current) {
				const spb = Math.max(1, Math.round(noteLengthRef.current / rhythmDenRef.current));
				if (idxRef.current % spb === 0) playMetronome(idxRef.current === 0);
			}
		};
		tick();
		intervalRef.current = setInterval(tick, computeIntervalMs(bpm, rhythmDen, noteLength));
	}

	useEffect(() => () => {
		clearInterval(intervalRef.current);
		clearInterval(templateIntervalRef.current);
	}, []);

	async function saveAsTemplate() {
		const isEmpty = patternStr.every(p => p === '');
		if (isEmpty) { alert('Vzorec je prazen!'); return; }
		const tplName = prompt('Ime predloge:');
		if (!tplName || !tplName.trim()) return;

		const payload = new URLSearchParams({
			name: tplName.trim(),
			rhythm: `${rhythmNum}/${rhythmDen}`,
			pattern: JSON.stringify(patternStr),
			bpm,
			note_length: noteLength,
		});
		try {
			const authSuffix = apiUser ? `?apiuser=${encodeURIComponent(apiUser)}&apikey=${encodeURIComponent(apiKey)}` : '';
			const res = await fetch(`${apiBase}save-strumming-template.html${authSuffix}`, { method: 'POST', body: payload });
			const data = await res.json();
			if (data.status === 'success' && data.response) {
				setTemplates(prev => [...prev, data.response]);
				setTemplatesOpen(true);
			}
		} catch (err) {
			console.error('Template save failed', err);
		}
	}

	async function deleteTemplate(i) {
		if (!confirm(`Izbriši predlogo "${templates[i].name}"?`)) return;
		if (playingTemplateIdx === i) stopTemplatePreview();
		const id = templates[i]?.id;
		if (!id) return;
		try {
			const authSuffix = apiUser ? `?apiuser=${encodeURIComponent(apiUser)}&apikey=${encodeURIComponent(apiKey)}` : '';
			const res = await fetch(`${apiBase}delete-strumming-template.html${authSuffix}`, {
				method: 'POST',
				body: new URLSearchParams({ id }),
			});
			const data = await res.json();
			if (data.status === 'success') {
				setTemplates(prev => prev.filter((_, idx) => idx !== i));
			}
		} catch (err) {
			console.error('Template delete failed', err);
		}
	}

	async function reorderTemplates(newTemplates) {
		setTemplates(newTemplates);
		const ids = newTemplates.map(t => t.id);
		try {
			const authSuffix = apiUser ? `?apiuser=${encodeURIComponent(apiUser)}&apikey=${encodeURIComponent(apiKey)}` : '';
			await fetch(`${apiBase}reorder-strumming-templates.html${authSuffix}`, {
				method: 'POST',
				body: new URLSearchParams({ ids: JSON.stringify(ids) }),
			});
		} catch (err) {
			console.error('Reorder failed', err);
		}
	}

	function playTemplate(i) {
		stopPlayback();
		if (playingTemplateIdx === i) { stopTemplatePreview(); return; }
		stopTemplatePreview();
		const tpl = templates[i];
		const [, den] = tpl.rhythm.split('/').map(Number);
		const nLen = tpl.note_length || tpl.noteLength || 8;
		const spb = Math.max(1, Math.round(nLen / den));
		const ms = (60 / (tpl.bpm || 100)) / spb * 1000;
		const pat = tpl.pattern;
		templateIdxRef.current = -1;
		setPlayingTemplateIdx(i);
		const tick = () => {
			templateIdxRef.current = (templateIdxRef.current + 1) % pat.length;
			const dir = pat[templateIdxRef.current];
			if (dir) playGuitarStrum(dir);
			if (metronomeRef.current && templateIdxRef.current % spb === 0) {
				playMetronome(templateIdxRef.current === 0);
			}
		};
		tick();
		templateIntervalRef.current = setInterval(tick, ms);
	}

	async function save() {
		if (!name.trim()) { alert('Vnesite ime vzorca'); return; }
		setSaving(true);

		const payload = new URLSearchParams({
			song_id,
			name: name.trim(),
			rhythm: `${rhythmNum}/${rhythmDen}`,
			pattern: JSON.stringify(patternStr),
			bpm,
			note_length: noteLength,
		});
		if (editingIdx !== null && patterns[editingIdx]?.id) {
			payload.set('id', patterns[editingIdx].id);
		}

		try {
			const authSuffix = apiUser ? `?apiuser=${encodeURIComponent(apiUser)}&apikey=${encodeURIComponent(apiKey)}` : '';
			const res = await fetch(`${apiBase}save-strumming-pattern.html${authSuffix}`, { method: 'POST', body: payload });
			const data = await res.json();

			if (data.status === 'success' && data.response) {
				let updated;
				if (editingIdx !== null) {
					updated = patterns.map((p, i) => i === editingIdx ? data.response : p);
					setPatterns(updated);
				} else {
					updated = [...patterns, data.response];
					setPatterns(updated);
					setEditingIdx(updated.length - 1);
				}
				window.dispatchEvent(new CustomEvent('strummingPatternsSaved', { detail: updated }));
			}
		} catch (err) {
			console.error('Save failed', err);
		}
		setSaving(false);
	}

	async function deletePattern() {
		if (editingIdx === null) return;
		if (!confirm('Izbriši vzorec?')) return;

		const id = patterns[editingIdx]?.id;
		if (!id) return;

		try {
			const authSuffix = apiUser ? `?apiuser=${encodeURIComponent(apiUser)}&apikey=${encodeURIComponent(apiKey)}` : '';
			const res = await fetch(`${apiBase}delete-strumming-pattern.html${authSuffix}`, {
				method: 'POST',
				body: new URLSearchParams({ id }),
			});
			const data = await res.json();

			if (data.status === 'success') {
				const updated = patterns.filter((_, i) => i !== editingIdx);
				setPatterns(updated);
				window.dispatchEvent(new CustomEvent('strummingPatternsSaved', { detail: updated }));
				newPattern();
			}
		} catch (err) {
			console.error('Delete failed', err);
		}
	}

	return (
		<div className="sp-editor-wrap">
			<div className="sp-pattern-list">
				{patterns.map((p, i) => (
					<button
						key={p.id ?? i}
						className={'sp-pattern-item' + (i === editingIdx ? ' sp-pattern-item-active' : '')}
						onClick={() => loadPattern(i)}
					>
						{p.name}
					</button>
				))}
				<button className="sp-pattern-item sp-pattern-item-new" onClick={newPattern}>
					+ Nov vzorec
				</button>
			</div>

			<div className="sp-editor-fields">
				<input
					className="sp-name-input"
					type="text"
					placeholder="Ime vzorca (npr. Vzorec 1)"
					value={name}
					onChange={e => setName(e.target.value)}
				/>
				<div className="sp-legend">
					Tipke: <kbd>↑</kbd> gor &nbsp; <kbd>↓</kbd> dol &nbsp; <kbd>B</kbd> bas &nbsp; <kbd>X</kbd> zadušeno &nbsp; <kbd>PRESLEDEK</kbd> pavza
				</div>
				<div className="sp-controls-row">
					<label>Ročno nastavljanje ritma</label>
					<button className="sp-play-btn-sm" onClick={togglePlay} title="Predvajaj vzorec">
						{isPlaying ? <StopIcon /> : <PlayIcon />}
					</button>
				</div>
				<PatternGrid
					patternStr={patternStr}
					notesStr={notesStr}
					rhythmNum={rhythmNum}
					rhythmDen={rhythmDen}
					noteLength={noteLength}
					activeIndex={activeIndex}
					focusedIndex={focusedIndex}
					isEditable={true}
					onCellClick={handleCellClick}
				/>
				<div className="sp-controls-row">
					<div className="sp-control-group">
						<label>Tempo</label>
						<div className="sp-bpm-row">
							<button className="sp-step-btn" onClick={() => setBpm(v => Math.max(1, v - 1))}>−</button>
							<input
								type="number"
								className="sp-bpm-input"
								value={bpm}
								min="1"
								max="360"
								onChange={e => setBpm(Math.min(360, Math.max(1, parseInt(e.target.value))))}
							/>
							<button className="sp-step-btn" onClick={() => setBpm(v => Math.min(360, v + 1))}>+</button>
							<span className="sp-bpm-label">bpm</span>
						</div>
					</div>

					<div className="sp-control-group">
						<label>Takt</label>
						<div className="sp-time-sig">
							<select value={rhythmNum} onChange={e => updateRhythm(parseInt(e.target.value), rhythmDen)}>
								{[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
							</select>
							<span>/</span>
							<select value={rhythmDen} onChange={e => updateRhythm(rhythmNum, parseInt(e.target.value))}>
								{[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
							</select>
						</div>
					</div>

					<div className="sp-control-group">
						<label>Dolžina not</label>
						<select
							className="sp-note-len-select"
							value={noteLength}
							onChange={e => updateNoteLength(parseInt(e.target.value))}
						>
							<option value={8}>1/8</option>
							<option value={16}>1/16</option>
						</select>
					</div>

					<div className="sp-control-group">
						<label>Metronom</label>
						<button
							className={'sp-metronome-btn' + (metronomeEnabled ? ' sp-metronome-active' : '')}
							onClick={() => setMetronomeEnabled(v => !v)}
							title="Vklopi/izklopi metronom"
						>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<path d="M9 4h6l4 17H5l4-17z" />
								<path d="M12 18L15 6" />
								<circle cx="13.5" cy="12" r="2.5" fill="currentColor" stroke="none" />
							</svg>
						</button>
					</div>
				</div>

				<div className="sp-actions">
					<button className="sp-save-btn" onClick={save} disabled={saving}>
						{saving ? 'Shranjujem…' : 'Shrani vzorec'}
					</button>
					<button className="sp-save-template-btn" onClick={saveAsTemplate}>
						Shrani kot predlogo
					</button>
					{editingIdx !== null && patterns[editingIdx]?.id && (
						<button className="sp-delete-btn" onClick={deletePattern}>
							Izbriši
						</button>
					)}
				</div>

				<div className="sp-templates-section">
					<button className="sp-templates-toggle" onClick={() => setTemplatesOpen(v => !v)}>
						<span>Predloge ({templates.length})</span>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
							style={{ transform: templatesOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
							<polyline points="6 9 12 15 18 9" />
						</svg>
					</button>
					{templatesOpen && (
						<div className="sp-templates-grid">
							{templates.length === 0 && (
								<div className="sp-templates-empty">Ni predlog. Shranite vzorec kot predlogo.</div>
							)}
							{templates.map((tpl, i) => {
								const isPreviewPlaying = playingTemplateIdx === i;
								return (
									<div
									key={tpl.id ?? i}
									className="sp-template-card"
									draggable
									onDragStart={() => { dragSrcIdx.current = i; }}
									onDragOver={e => e.preventDefault()}
									onDrop={e => {
										e.preventDefault();
										const from = dragSrcIdx.current;
										if (from === null || from === i) return;
										const newTpls = [...templates];
										const [moved] = newTpls.splice(from, 1);
										newTpls.splice(i, 0, moved);
										if (playingTemplateIdx === from) {
											setPlayingTemplateIdx(i);
										} else if (from < i && playingTemplateIdx > from && playingTemplateIdx <= i) {
											setPlayingTemplateIdx(playingTemplateIdx - 1);
										} else if (from > i && playingTemplateIdx >= i && playingTemplateIdx < from) {
											setPlayingTemplateIdx(playingTemplateIdx + 1);
										}
										reorderTemplates(newTpls);
										dragSrcIdx.current = null;
									}}
								>
									<div className="sp-template-header">
										<span className="sp-template-drag-handle" title="Povleci za preurejanje">⠿</span>
										<span className="sp-template-name">{tpl.name}</span>
										<div className="sp-template-btns">
												<button
													className="sp-template-play-btn"
													title={isPreviewPlaying ? 'Ustavi' : 'Predvajaj'}
													onClick={e => { e.stopPropagation(); playTemplate(i); }}
												>
													{isPreviewPlaying ? <MiniStopIcon /> : <MiniPlayIcon />}
												</button>
												<button
													className="sp-template-delete-btn"
													title="Izbriši predlogo"
													onClick={e => { e.stopPropagation(); deleteTemplate(i); }}
												>
													×
												</button>
											</div>
										</div>
										<div className="sp-template-meta">
											{tpl.rhythm} · 1/{tpl.note_length} · {tpl.bpm || 100} bpm
										</div>
										<div className="sp-template-arrows">
											{tpl.pattern.map((s, j) => (
												<span key={j} className={'sp-tpl-arrow' + (s === 'U' ? ' up' : s === 'X' ? ' mute' : s === 'B' ? ' bass' : '')}>
													{s === 'D' ? '↓' : s === 'U' ? '↑' : s === 'B' ? 'B' : s === 'X' ? 'X' : '·'}
												</span>
											))}
										</div>
										<button className="sp-template-load-btn" onClick={() => loadFromTemplate(tpl)}>
											Naloži v urejevalnik
										</button>
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
